// Types
import { FETCH_SELECTED_FORM, UPDATE_SELECTED_FORM, UPDATE_SELECTED_FORM_ETAG, REJECT_SELECTED_FORM } from "constants/types";

// Actions
import { showSnackbarMessage } from "actions/SnackbarActions";

// Helpers
import { getEnvironmentVariable } from "helpers/environmentVariableHelpers.js";
import { apiRequest } from "helpers/apiClient";

const getKommunenavnAsync = (form) => {
    return new Promise((resolve, reject) => {
        if (
            form?.formData?.eiendomByggesteder?.[0]?.kommunenavn &&
            form?.formData?.eiendomByggesteder?.[0]?.kommunenavn.length
        ) {
            resolve(form.formData.eiendomByggesteder[0].kommunenavn);
        } else if (
            form?.formData?.eiendomByggesteder?.[0]?.kommunenummer &&
            !isNaN(form?.formData?.eiendomByggesteder?.[0]?.kommunenummer) &&
            form?.formData?.eiendomByggesteder?.[0]?.kommunenummer?.toString().length === 4
        ) {
            const kommunenummer = form.formData.eiendomByggesteder[0].kommunenummer;
            const apiUrl = `${getEnvironmentVariable("admbyggApiUrl")}/municipalities/${kommunenummer}`;
            return fetch(apiUrl)
                .then((res) => res.json())
                .then((municipality) => {
                    resolve(municipality?.Name);
                })
                .catch((error) => {
                    console.warn("Finner ikke kommunenavn:", error);
                    resolve(null);
                });
        } else {
            resolve(null);
        }
    });
};

export const fetchSelectedForm = (submission) => (dispatch) => {
    const formType = submission?.innsendingstype?.toLowerCase();
    const formPath = submission._links?.[formType]?.href;
    if (formPath?.length) {
        return apiRequest(formPath).then((result) => {
            // Skjema-GET feilet (404/500) - returner null så kalleren kan vise en feilmelding i stedet
            // for å NPE på manglende data.
            if (!result.ok || !result.data) {
                return null;
            }
            const form = result.data;
            // Ta vare på ETag (optimistisk samtidighets-token, sendes som If-Match ved lagring) og
            // berik med kommunenavn før dispatch.
            return getKommunenavnAsync(form).then((kommunenavn) => {
                if (form.formData?.eiendomByggesteder?.[0]) {
                    form.formData.eiendomByggesteder[0].kommunenavn = kommunenavn;
                }
                form.etag = result.etag;
                dispatch({ type: FETCH_SELECTED_FORM, payload: form });
                return form;
            });
        });
    } else {
        // Ingen skjemalenke på innsendingen (f.eks. hvis innsendingshentingen feilet eller mangler
        // _links[innsendingstype]). Returner et løst promise (ikke null) slik at kallere trygt kan
        // gjøre .then(...) - de tolker null-resultatet som "kunne ikke hente skjema".
        return Promise.resolve(null);
    }
};

export const updateSelectedForm = (form) => (dispatch) => {
    return new Promise((resolve) => {
        dispatch({ type: UPDATE_SELECTED_FORM, payload: form });
        resolve(form);
    });
};

// Oppdaterer kun samtidighets-token (etag) på gjeldende skjema i storen, uten å røre skjemadataene.
// Brukes etter en vellykket lagring der brukeren har gjort nye endringer imens - da beholder vi de
// nye endringene men frisker opp etag-en så neste lagring ikke får en falsk 412.
export const updateSelectedFormEtag = (etag) => (dispatch) => {
    dispatch({ type: UPDATE_SELECTED_FORM_ETAG, payload: etag });
};

export const saveSelectedForm = (form, accessToken, stage) => (dispatch) => {
    const abortController = new AbortController();
    const path = `${form._links?.self?.href}${stage?.length ? `?stage=${stage}` : ""}`;
    return {
        abortController: abortController,
        // apiRequest kaster aldri (avbrudd/nettverksfeil normaliseres), så `ready` avvises aldri -
        // saveForm i Form.js har ingen .catch og er avhengig av det.
        ready: apiRequest(path, {
            method: "PUT",
            accessToken,
            body: form,
            // Optimistisk samtidighet: si fra hvilken versjon vi redigerer. Serveren svarer 412
            // dersom erklæringen er endret et annet sted siden vi hentet den.
            headers: { ...(form?.etag ? { "If-Match": form.etag } : {}) },
            signal: abortController.signal
        }).then((result) => {
            // Avbrutt fordi en nyere lagring overtok - ikke en feil, ikke prøv på nytt.
            if (result.aborted) {
                return { aborted: true };
            }
            if (result.ok) {
                const savedForm = result.data || {};
                savedForm.etag = result.etag;
                return { status: result.status, data: savedForm };
            }
            if (result.networkError) {
                return { status: 0, networkError: true };
            }
            return { status: result.status, xCorrelationId: result.xCorrelationId };
        })
    };
};

export const rejectSelectedForm = (form, rejectionMessage, accessToken) => (dispatch) => {
    dispatch(showSnackbarMessage("Avviser erklæring", 3000));
    return apiRequest(form._links?.avvis?.href, {
        method: "POST",
        accessToken,
        body: { reason: rejectionMessage }
    }).then((result) => {
        if (result.ok) {
            dispatch(showSnackbarMessage("Erklæringen er avvist", 3000));
            dispatch({ type: REJECT_SELECTED_FORM, payload: result.data });
            return {
                status: result.status,
                data: result.data
            };
        }
        return {
            status: result.status,
            xCorrelationId: result.xCorrelationId
        };
    });
};
